import { useEstimate } from '../context/EstimateContext'
import { COMPANY } from '../config/constants'

const STAGE_INDEX = {
  symptom: 1,
  photo: 2,
  extracting: 2,
  verdict: 3,
  booking: 4,
  confirmation: 5,
}
const TOTAL_STAGES = 5
const CAN_GO_BACK = new Set(['photo', 'verdict', 'booking'])

function Droplet() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor" aria-hidden="true">
      <path d="M12 2.5c-.35 0-.67.18-.85.48C9.9 5.02 5 12.06 5 15.5a7 7 0 1 0 14 0c0-3.44-4.9-10.48-6.15-12.52A.99.99 0 0 0 12 2.5Z" />
    </svg>
  )
}

function BackChevron() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 3h3l1.5 4.5-2 1.5a12 12 0 0 0 6 6l1.5-2 4.5 1.5v3a2 2 0 0 1-2 2A16.5 16.5 0 0 1 4.5 5a2 2 0 0 1 2-2z" />
    </svg>
  )
}

export default function Shell({ children }) {
  const { step, goBack } = useEstimate()
  const stage = STAGE_INDEX[step] || 1

  return (
    // data-embed-root is the STABLE hook the iframe auto-resize CSS targets to
    // neutralize this shell's min-h-full (see src/lib/embed.js). We select this
    // attribute rather than the .min-h-full utility class so the neutralization
    // can never leak onto other .min-h-full / .h-full elements (e.g. the
    // ExtractingStep reactor rings). Present in every render; only acted on when
    // embedded.
    <div data-embed-root className="flex min-h-full flex-col">
      <header className="sticky top-0 z-20 border-b border-hairline bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-md items-center gap-3 px-4 py-3">
          {CAN_GO_BACK.has(step) ? (
            <button
              onClick={goBack}
              className="-ml-1 rounded-xl p-2 text-muted transition hover:bg-slate-100 hover:text-ink"
              aria-label="Go back"
            >
              <BackChevron />
            </button>
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-grad shadow-glow">
              <Droplet />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-sm font-bold leading-tight text-ink">{COMPANY.name}</p>
            <p className="text-[11px] leading-tight text-muted">Instant Estimate</p>
          </div>
          <a
            href={`tel:${COMPANY.phone.replace(/[^\d+]/g, '')}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white/70 px-3.5 py-2 text-xs font-semibold text-subink transition hover:bg-white"
          >
            <PhoneIcon />
            Call
          </a>
        </div>

        {step !== 'confirmation' && (
          <div className="mx-auto w-full max-w-md px-4 pb-3">
            <span className="eyebrow">
              Step {stage} <span className="text-slate-300">/</span> {TOTAL_STAGES}
            </span>
            <div className="mt-1.5 flex gap-1.5" aria-hidden="true">
              {Array.from({ length: TOTAL_STAGES }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                    i < stage ? 'bg-brand-grad' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-md flex-1 px-4 py-6">{children}</main>
    </div>
  )
}
